import { createTheme } from '@mui/material';

export const brandPalette = {
    blue: '#13154F',
    amber: '#FFA300',
    gray: '#EEEEEE',
    graySecondary: '#DDDDDD'
};

const modeTokens = {
    dark: {
        mode: 'dark',
        background: '#090a24',
        paper: 'rgba(255, 255, 255, 0.08)',
        paperStrong: 'rgba(255, 255, 255, 0.12)',
        text: '#ffffff',
        muted: 'rgba(255, 255, 255, 0.66)',
        line: 'rgba(255, 255, 255, 0.13)',
        input: 'rgba(255, 255, 255, 0.08)',
        inputHover: 'rgba(255, 255, 255, 0.12)',
        table: 'rgba(10, 12, 43, 0.92)',
        shadow: '0 24px 52px rgba(0, 0, 0, 0.16)',
        popup: 'rgba(10, 12, 43, 0.96)',
        popupText: '#ffffff',
        popupMuted: 'rgba(255, 255, 255, 0.66)',
        popupLine: 'rgba(255, 255, 255, 0.13)',
        popupShadow: '0 24px 52px rgba(0, 0, 0, 0.22)'
    },
    light: {
        mode: 'light',
        background: brandPalette.gray,
        paper: 'rgba(255, 255, 255, 0.78)',
        paperStrong: '#ffffff',
        text: brandPalette.blue,
        muted: '#5f6680',
        line: 'rgba(19, 21, 79, 0.12)',
        input: 'rgba(255, 255, 255, 0.82)',
        inputHover: '#ffffff',
        table: 'rgba(255, 255, 255, 0.96)',
        shadow: '0 24px 52px rgba(19, 21, 79, 0.08)',
        popup: '#ffffff',
        popupText: brandPalette.blue,
        popupMuted: '#5f6680',
        popupLine: 'rgba(19, 21, 79, 0.12)',
        popupShadow: '0 24px 52px rgba(19, 21, 79, 0.18)'
    }
};

export const createAppTheme = (mode = 'dark') => {
    const tokens = modeTokens[mode] || modeTokens.dark;

    return createTheme({
        palette: {
            mode: tokens.mode,
            primary: {
                main: brandPalette.blue,
                dark: '#090a24',
                light: '#2a2d78',
                contrastText: '#ffffff'
            },
            secondary: {
                main: brandPalette.amber,
                contrastText: brandPalette.blue
            },
            background: {
                default: tokens.background,
                paper: tokens.paper
            },
            text: {
                primary: tokens.text,
                secondary: tokens.muted
            },
            divider: tokens.line
        },
        typography: {
            fontFamily: '"Telegraf", "Inter", "Gotham", sans-serif',
            h1: { fontFamily: '"Telegraf", "Gotham Bold", "Gotham", sans-serif', fontWeight: 800, letterSpacing: 0 },
            h2: { fontFamily: '"Telegraf", "Gotham Bold", "Gotham", sans-serif', fontWeight: 800, letterSpacing: 0 },
            h3: { fontFamily: '"Telegraf", "Gotham Bold", "Gotham", sans-serif', fontWeight: 800, letterSpacing: 0 },
            h4: { fontFamily: '"Telegraf", "Gotham Bold", "Gotham", sans-serif', fontWeight: 800, letterSpacing: 0 },
            h5: { fontFamily: '"Telegraf", "Gotham Bold", "Gotham", sans-serif', fontWeight: 800, letterSpacing: 0 },
            h6: { fontFamily: '"Telegraf", "Gotham Bold", "Gotham", sans-serif', fontWeight: 800, letterSpacing: 0 },
            button: {
                fontFamily: '"Telegraf", "Inter", "Gotham", sans-serif',
                fontWeight: 800,
                letterSpacing: 0,
                textTransform: 'none'
            },
            overline: {
                color: brandPalette.amber,
                fontSize: '0.68rem',
                fontWeight: 800,
                letterSpacing: '0.12em',
                lineHeight: 1.5,
                textTransform: 'uppercase'
            }
        },
        shape: {
            borderRadius: 18
        },
        components: {
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 14,
                        boxShadow: 'none',
                        fontWeight: 800,
                        padding: '10px 16px',
                        transition: 'transform 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease',
                        '&:hover': {
                            transform: 'translateY(-1px)'
                        }
                    },
                    contained: {
                        background: `linear-gradient(135deg, ${brandPalette.amber} 0%, #ffd37a 100%)`,
                        border: '1px solid rgba(255, 163, 0, 0.42)',
                        color: brandPalette.blue,
                        boxShadow: '0 14px 28px rgba(255, 163, 0, 0.2)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #ffb22d 0%, #ffd890 100%)',
                            boxShadow: '0 18px 32px rgba(255, 163, 0, 0.28)'
                        },
                        '&.Mui-disabled': {
                            background: 'rgba(255, 163, 0, 0.34)',
                            color: 'rgba(19, 21, 79, 0.58)'
                        }
                    },
                    outlined: {
                        borderColor: tokens.line,
                        color: tokens.text,
                        '&:hover': {
                            borderColor: 'rgba(255, 163, 0, 0.55)',
                            backgroundColor: 'rgba(255, 163, 0, 0.08)'
                        }
                    }
                }
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        border: `1px solid ${tokens.line}`,
                        borderRadius: 20,
                        background: `${tokens.paper} !important`,
                        boxShadow: `${tokens.shadow} !important`,
                        color: tokens.text,
                        backdropFilter: 'blur(22px)'
                    }
                }
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        borderColor: tokens.line,
                        backgroundImage: 'none'
                    }
                }
            },
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        color: tokens.text,
                        transition: 'transform 0.18s ease, background-color 0.18s ease',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 163, 0, 0.1)',
                            transform: 'translateY(-1px)'
                        }
                    }
                }
            },
            MuiFormControl: {
                styleOverrides: {
                    root: {
                        '& .MuiInputLabel-root': {
                            color: tokens.muted,
                            fontFamily: '"Telegraf", "Inter", "Gotham", sans-serif',
                            fontWeight: 700,
                            '&.Mui-focused': {
                                color: brandPalette.amber
                            }
                        }
                    }
                }
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: 16,
                        background: tokens.input,
                        color: tokens.text,
                        fontFamily: '"Telegraf", "Inter", "Gotham", sans-serif',
                        fontWeight: 600,
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: tokens.line
                        },
                        '&:hover': {
                            background: tokens.inputHover
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 163, 0, 0.42)'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: brandPalette.amber,
                            borderWidth: 1
                        }
                    },
                    input: {
                        '&::placeholder': {
                            color: tokens.muted,
                            opacity: 1
                        }
                    }
                }
            },
            MuiSelect: {
                styleOverrides: {
                    icon: {
                        color: tokens.muted
                    }
                }
            },
            MuiMenu: {
                styleOverrides: {
                    paper: {
                        border: `1px solid ${tokens.popupLine}`,
                        borderRadius: 18,
                        background: `${tokens.popup} !important`,
                        boxShadow: tokens.popupShadow,
                        backdropFilter: 'blur(22px)',
                        color: tokens.popupText
                    },
                    list: {
                        padding: 8
                    }
                }
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: {
                        borderRadius: 12,
                        color: tokens.popupText,
                        fontFamily: '"Telegraf", "Inter", "Gotham", sans-serif',
                        fontWeight: 700,
                        '&:hover': {
                            backgroundColor: 'rgba(255, 163, 0, 0.1)'
                        },
                        '&.Mui-selected': {
                            backgroundColor: 'rgba(255, 163, 0, 0.14)'
                        }
                    }
                }
            },
            MuiAutocomplete: {
                styleOverrides: {
                    paper: {
                        border: `1px solid ${tokens.popupLine}`,
                        borderRadius: 18,
                        background: tokens.popup,
                        boxShadow: tokens.popupShadow,
                        color: tokens.popupText,
                        backdropFilter: 'blur(22px)'
                    },
                    listbox: {
                        padding: 8,
                        color: tokens.popupText,
                        '& .MuiAutocomplete-option': {
                            borderRadius: 12,
                            fontFamily: '"Telegraf", "Inter", "Gotham", sans-serif',
                            fontWeight: 700
                        },
                        '& .MuiAutocomplete-option.Mui-focused': {
                            backgroundColor: 'rgba(255, 163, 0, 0.1)'
                        },
                        '& .MuiAutocomplete-option[aria-selected="true"]': {
                            backgroundColor: 'rgba(255, 163, 0, 0.14)'
                        }
                    },
                    noOptions: {
                        color: tokens.popupMuted,
                        fontFamily: '"Telegraf", "Inter", "Gotham", sans-serif',
                        fontWeight: 700
                    },
                    loading: {
                        color: tokens.popupMuted
                    }
                }
            },
            MuiPickersPopper: {
                styleOverrides: {
                    paper: {
                        border: `1px solid ${tokens.popupLine}`,
                        borderRadius: 22,
                        background: tokens.popup,
                        boxShadow: tokens.popupShadow,
                        color: tokens.popupText,
                        backdropFilter: 'blur(22px)'
                    }
                }
            },
            MuiPickersLayout: {
                styleOverrides: {
                    root: {
                        background: 'transparent',
                        color: tokens.popupText
                    }
                }
            },
            MuiDateCalendar: {
                styleOverrides: {
                    root: {
                        background: 'transparent',
                        color: tokens.popupText
                    }
                }
            },
            MuiPickersDay: {
                styleOverrides: {
                    root: {
                        color: tokens.popupText,
                        fontFamily: '"Telegraf", "Inter", "Gotham", sans-serif',
                        fontWeight: 700,
                        '&:hover': {
                            backgroundColor: 'rgba(255, 163, 0, 0.12)'
                        },
                        '&.Mui-selected': {
                            backgroundColor: `${brandPalette.amber} !important`,
                            color: `${brandPalette.blue} !important`
                        }
                    }
                }
            },
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        borderBottom: `1px solid ${tokens.line}`,
                        color: tokens.text,
                        fontFamily: '"Telegraf", "Inter", "Gotham", sans-serif'
                    },
                    head: {
                        color: tokens.muted,
                        fontSize: '0.68rem',
                        fontWeight: 800,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase'
                    }
                }
            },
            MuiLinearProgress: {
                styleOverrides: {
                    root: {
                        borderRadius: 999,
                        backgroundColor: tokens.line
                    },
                    bar: {
                        borderRadius: 999,
                        backgroundColor: brandPalette.amber
                    }
                }
            },
            MuiFormControlLabel: {
                styleOverrides: {
                    root: {
                        '& .MuiSwitch-root + .MuiFormControlLabel-label': {
                            marginLeft: 10
                        }
                    },
                    label: {
                        fontFamily: '"Telegraf", "Inter", "Gotham", sans-serif'
                    }
                }
            },
            MuiSwitch: {
                styleOverrides: {
                    root: {
                        width: 46,
                        height: 30,
                        padding: 0,
                        overflow: 'visible'
                    },
                    switchBase: {
                        padding: 4,
                        transition: 'transform 0.18s ease',
                        '&.Mui-checked': {
                            color: brandPalette.amber,
                            transform: 'translateX(16px)'
                        },
                        '&.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: brandPalette.amber
                        }
                    },
                    thumb: {
                        width: 22,
                        height: 22,
                        boxShadow: '0 5px 12px rgba(0, 0, 0, 0.22)'
                    },
                    track: {
                        borderRadius: 999,
                        backgroundColor: 'rgba(255, 255, 255, 0.22)',
                        opacity: 1
                    }
                }
            },
            MuiDataGrid: {
                styleOverrides: {
                    root: {
                        border: `1px solid ${tokens.line}`,
                        borderRadius: 18,
                        color: tokens.text,
                        fontFamily: '"Telegraf", "Inter", "Gotham", sans-serif',
                        background: tokens.table
                    },
                    columnHeaders: {
                        color: tokens.muted,
                        fontWeight: 800,
                        textTransform: 'uppercase'
                    },
                    cell: {
                        borderColor: tokens.line
                    },
                    footerContainer: {
                        borderColor: tokens.line
                    }
                }
            }
        }
    });
};

export const theme = createAppTheme('dark');
